import { useEffect, useRef, useCallback } from 'react';

/**
 * useSwipeBack
 * 
 * Detects right-swipe gesture from the left edge of the screen
 * to trigger back navigation. Designed for iOS PWA standalone mode
 * where the native swipe-back gesture is not available.
 * 
 * Provides visual feedback during the swipe with a subtle page shift
 * to make the gesture feel native.
 * 
 * Usage:
 *   useSwipeBack({
 *     onSwipeBack: () => goBack(),
 *     enabled: true,
 *     threshold: 80,
 *     edgeWidth: 30,
 *   });
 */
export function useSwipeBack({
  onSwipeBack,
  enabled = true,
  threshold = 80,
  edgeWidth = 30,
}) {
  const touchStartRef = useRef(null);
  const touchMoveRef = useRef(null);
  const isEdgeSwipeRef = useRef(false);
  const pageContentRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    if (!enabled) return;
    const touch = e.touches[0];
    // Only trigger if touch starts from the left edge
    if (touch.clientX <= edgeWidth) {
      touchStartRef.current = { x: touch.clientX, y: touch.clientY };
      isEdgeSwipeRef.current = true;
      // Cache the page content element for visual feedback
      pageContentRef.current = document.querySelector('.page-content');
    } else {
      isEdgeSwipeRef.current = false;
    }
  }, [enabled, edgeWidth]);

  const handleTouchMove = useCallback((e) => {
    if (!isEdgeSwipeRef.current || !touchStartRef.current) return;
    const touch = e.touches[0];
    touchMoveRef.current = { x: touch.clientX, y: touch.clientY };

    // Provide visual feedback during swipe
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);

    // Only apply visual feedback if swipe is primarily horizontal
    if (deltaX > 0 && deltaY < deltaX * 0.7 && pageContentRef.current) {
      const progress = Math.min(deltaX / threshold, 1);
      const translateX = Math.min(deltaX * 0.3, 30); // Max 30px shift
      const opacity = 1 - (progress * 0.1); // Subtle opacity change
      pageContentRef.current.style.transform = `translateX(${translateX}px)`;
      pageContentRef.current.style.opacity = opacity;
      pageContentRef.current.style.transition = 'none';
    }
  }, [threshold]);

  const handleTouchEnd = useCallback(() => {
    // Reset visual feedback
    if (pageContentRef.current) {
      pageContentRef.current.style.transform = '';
      pageContentRef.current.style.opacity = '';
      pageContentRef.current.style.transition = 'transform 0.2s ease, opacity 0.2s ease';
      // Clean up transition style after it completes
      setTimeout(() => {
        if (pageContentRef.current) {
          pageContentRef.current.style.transition = '';
        }
      }, 200);
    }

    if (!isEdgeSwipeRef.current || !touchStartRef.current || !touchMoveRef.current) {
      touchStartRef.current = null;
      touchMoveRef.current = null;
      isEdgeSwipeRef.current = false;
      return;
    }

    const deltaX = touchMoveRef.current.x - touchStartRef.current.x;
    const deltaY = Math.abs(touchMoveRef.current.y - touchStartRef.current.y);

    // Swipe must be primarily horizontal and exceed threshold
    if (deltaX > threshold && deltaY < deltaX * 0.5) {
      onSwipeBack();
    }

    touchStartRef.current = null;
    touchMoveRef.current = null;
    isEdgeSwipeRef.current = false;
  }, [onSwipeBack, threshold]);

  useEffect(() => {
    if (!enabled) return;

    // Only enable in standalone mode (iOS PWA)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;

    if (!isStandalone) return;

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);
}

export default useSwipeBack;
